import { useMemo, useState, useEffect } from "react";
import { useAuth } from "../app/useAuth";
import { useFriends } from "./useFriends";
import { useSchedulersByParticipant, useSchedulersByGroupIds } from "./useSchedulers";
import { useQuestingGroups } from "./useQuestingGroups";
import { findUserIdByEmail } from "../lib/data/users";
import {
  acceptedFriendRequestsFromQuery,
  acceptedFriendRequestsToQuery,
} from "../lib/data/friends";
import { useFirestoreCollection } from "./useFirestoreCollection";
import { normalizeEmail } from "../lib/utils";

/**
 * Fetches shared polls, shared groups, and mutual friends between
 * the current user and a target user (by email).
 * Privacy: only returns data where BOTH users are participants.
 */
export function useSharedProfileData(targetEmail) {
  const { user } = useAuth();
  const { friends: currentUserFriends } = useFriends();
  const mySchedulers = useSchedulersByParticipant(user?.uid);
  const { groups: myGroups, getGroupColor } = useQuestingGroups();

  const normalizedTarget = normalizeEmail(targetEmail);

  // Resolve target user's ID
  const [resolveState, setResolveState] = useState({ id: null, loading: false });

  useEffect(() => {
    if (!normalizedTarget) return;
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: synchronous state init/reset for this subscription effect
    setResolveState({ id: null, loading: true });
    findUserIdByEmail(normalizedTarget)
      .then((id) => {
        if (!cancelled) setResolveState({ id, loading: false });
      })
      .catch(() => {
        if (!cancelled) setResolveState({ id: null, loading: false });
      });
    return () => {
      cancelled = true;
    };
  }, [normalizedTarget]);

  const targetUserId = normalizedTarget ? resolveState.id : null;
  const resolving = normalizedTarget ? resolveState.loading : false;

  // Shared groups: groups where target user is also a member
  const sharedGroups = useMemo(() => {
    if (!targetUserId || !myGroups) return [];
    return myGroups.filter(
      (g) => Array.isArray(g.memberIds) && g.memberIds.includes(targetUserId)
    );
  }, [myGroups, targetUserId]);

  // Shared polls: schedulers where target user is also a participant (direct or via group)
  const sharedGroupIds = useMemo(() => sharedGroups.map((g) => g.id), [sharedGroups]);
  const groupSchedulers = useSchedulersByGroupIds(sharedGroupIds);

  const sharedPolls = useMemo(() => {
    const deduped = new Map();
    // Direct: target user in participantIds
    if (targetUserId && mySchedulers.data) {
      mySchedulers.data
        .filter((s) => Array.isArray(s.participantIds) && s.participantIds.includes(targetUserId))
        .forEach((s) => deduped.set(s.id, s));
    }
    // Group-linked: schedulers from shared groups
    groupSchedulers.data.forEach((s) => deduped.set(s.id, s));
    return Array.from(deduped.values());
  }, [mySchedulers.data, targetUserId, groupSchedulers.data]);

  // Target user's friends (for mutual friends computation)
  const targetAcceptedFromRef = useMemo(() => {
    if (!normalizedTarget) return null;
    return acceptedFriendRequestsFromQuery(normalizedTarget);
  }, [normalizedTarget]);

  const targetAcceptedToRef = useMemo(() => {
    if (!normalizedTarget) return null;
    return acceptedFriendRequestsToQuery(normalizedTarget);
  }, [normalizedTarget]);

  const targetAcceptedFrom = useFirestoreCollection(targetAcceptedFromRef);
  const targetAcceptedTo = useFirestoreCollection(targetAcceptedToRef);

  const mutualFriends = useMemo(() => {
    const targetFriendEmails = new Set();
    targetAcceptedFrom.data.forEach((r) => {
      if (r?.toEmail) targetFriendEmails.add(normalizeEmail(r.toEmail));
    });
    targetAcceptedTo.data.forEach((r) => {
      if (r?.fromEmail) targetFriendEmails.add(normalizeEmail(r.fromEmail));
    });
    // Remove target email itself from their own friend list
    targetFriendEmails.delete(normalizedTarget);

    const myFriendSet = new Set(
      currentUserFriends.map((e) => normalizeEmail(e)).filter(Boolean)
    );
    return Array.from(targetFriendEmails).filter((e) => myFriendSet.has(e));
  }, [
    targetAcceptedFrom.data,
    targetAcceptedTo.data,
    currentUserFriends,
    normalizedTarget,
  ]);

  const groupsById = useMemo(() => {
    const map = {};
    (myGroups || []).forEach((g) => {
      map[g.id] = g;
    });
    return map;
  }, [myGroups]);

  const loading =
    resolving ||
    mySchedulers.loading ||
    groupSchedulers.loading ||
    targetAcceptedFrom.loading ||
    targetAcceptedTo.loading;

  return { sharedPolls, sharedGroups, mutualFriends, getGroupColor, groupsById, loading };
}
